/**
 * @file
 * Tired of writing the same tired profile endpoints to do the same old simple
 * actions such as liking, hiding, bookmarking etc? Then make things simple with
 * ProfileActions. Just make sure you bind to the corresponding okevents.
 *
 * This file also autobinds profile actions to nodes with certain attributes.
 * Since this file is included in desktop's common.js, you should never have to
 * bind profile events; instead just follow this example.
 *
 * @example
 * // I'm doing this the react way, but you can also do this with plain HTML.
 *
 * const tuid = params.userid;
 *
 * return (
 *   <div data-profile-action="hide" data-tuid={tuid}></div>
 * );
 */

/* eslint
		block-function-spacing/space-before-function-props: 0,
		no-alert: 0
*/

/* global
		_str,
		OkLike,
		FeedbackBanner,
		MessagesPage,
		MessageDropdown,
*/

import React from "react";
import ReactDOM from "react-dom";
import queryString from "query-string";
import $ from "jquery";
import ConfirmationModal, { dispatch } from "shared/components/ConfirmationModal";
import OnDOMReady from "shared/util/OnDOMReady";
import Analytics from "analytics/util/Analytics";
import CUSTOM_EVENT_TYPES from "analytics/util/CustomEventTypes";
import OkC from "shared/util/OkcApi";
import PhotoUtil from "shared/util/PhotoUtil";
import GetUserIdFromWindowLocation from "shared/util/GetUserIdFromWindowLocation";
import GlobalMessaging from "features/global_messaging";
import {
	OKEVENT,
	triggerOkEvent,
} from "shared/util/OkEventHelpers";
import Report, { TYPES } from "shared/util/Report";
import { PROMO_DESIGN } from "analytics/util/queryParamsMap";
import goToUrl from "shared/util/goToUrl";

const HIDE_STORAGE_ITEM = "hasSeenHideConfirmation";

const showError = (msg, type = "error") => {
	if (window.FeedbackBanner) {
		FeedbackBanner.open({
			message: msg,
			type,
			timeout: 2000,
		});
	} else {
		alert(msg);
	}
};

// a helper to generate analytics bundles for profile actions
const getEventData = (params) => {
	const eventData = { ...params };

	if (
		(eventData.value === "blocked match" || eventData.value === "reported match") &&
		GlobalMessaging.isConversationOpen(eventData.userid)
	) {
		eventData.source = "conversation";
	} else if (window.location.pathname.indexOf("/profile") === 0) {
		eventData.source = "match profile";
	} else if (window.location.pathname.indexOf("/match") === 0) {
		eventData.source = "search";
	} else if (window.location.pathname.indexOf("/messages") === 0) {
		eventData.source = "conversation";
	} else {
		eventData.source = window.location.pathname.replace(/-/g, " ").replace("/", "");
	}

	return eventData;
};

// the first time you click on a profile action, the shit is in the DOM.
// henceforth, it's really hard to keep all these actions in-sync.
// so make a mutable store of tuids and their information the first time we hit the DOM
// and check that afterwards.

// this will map userids to { wasLiked, wasPassed }
const buttonData = {};

/**
 * @param  {String} userid - The userid of the user being liked.
 * @param  {Object} params - Addl params
 * @param  {Bool} params.wasPassed - If, before clicking the link, the user had passed.
 * @param  {Bool} params.wasLiked - If, before clicking the link, the user had liked.
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 * @param  {Bool} params.needsExplanation - Shows a confirmation modal if true.
 */

let storedObjectLike = null;

export function like(
	userid,
	{
		wasLiked,
		matchPercentage,
		objectLike,
		firstMessage,
		photos,
		displayname,
		username,
		currentUserThumb,
		needsExplanation,
	} = {},
	onErrorCallback,
) {
	if (!userid) { return; }

	// if they already liked this person, do nothing
	if (wasLiked) { return; }

	const handleFailure = () => {
		showError(_str("Unable to like them"));
		triggerOkEvent(OKEVENT.VoteFailure, { tuid: userid });
	};

	const isOnUserProfile = GetUserIdFromWindowLocation() === userid
		|| GetUserIdFromWindowLocation() === username;

	let userData;

	// Get any user_data we may have passed in the URL,
	// if we're on the user's profile (this is just a sanity check).
	if (isOnUserProfile) {
		userData = queryString.parse(window.location.search).user_data;
	}

	const doLike = () => {
		OkC.api("/likes/batch", {
			api: 1,
			type: "POST",
			data: {
				source: "PROFILE",
				votes: [
					{
						userid,
						like: true,
						time_from_request: 0,
						user_data: userData,
					},
				],
			},
			success(res) {
				const [data] = res.results;

				if (res.shouldTrackHitLikesCap) {
					Analytics.logUIEvent(Analytics.eventNames.reachedDailyLikes);
				}

				// response always returns 10,000 likesRemaining if
				// user has unlimited likes
				if (!data && (res.likesRemaining || !onErrorCallback)) {
					handleFailure();
					return;
				} else if (!data && !res.likesRemaining) {
					Analytics.logUIEvent(Analytics.eventNames.votedAfterLikesCapLimit, {
						data: {
							"promo id": "alist.likescappaywall",
							"promo design": PROMO_DESIGN.profilephoto,
							"target user id": userid,
							"vote value": "like",
							source: "profile",
						},
					});
					onErrorCallback();
					return;
				}

				buttonData[data.userid] = {
					wasLiked: "true",
					wasPassed: "null",
				};

				const likeObj = objectLike || storedObjectLike;

				triggerOkEvent(OKEVENT.Vote, {
					tuid: data.userid,
					liked: true,
					mutual: data.mutual_like,
					viaBoost: data.via_spotlight,
					objectLike: likeObj,
					theirThumb: photos && photos[0] && photos[0]["400x400"] ? photos[0]["400x400"] : PhotoUtil.getPlaceholder(400),
					theirUsername: displayname || username,
					currentUserThumb,
				});

				let profileLikeType = false;

				if (likeObj && likeObj.comment_type) {
					profileLikeType = likeObj.comment_type;
				}

				storedObjectLike = null;

				Analytics.logUIEvent(Analytics.eventNames.profileInteraction, {
					type: CUSTOM_EVENT_TYPES.user_content,
					data: getEventData({
						interaction: "like",
						userid,
						profileLikeType,
						"message flag": firstMessage ? "yes" : "no",
						"boosted like": data.via_spotlight ? "yes" : "no",
						"resulted in mutual like": data.mutual_like ? "yes" : "no",
						matchPercentage,
					}),
				});
			},
			error: handleFailure,
		});
	};

	// if they haven't seen the user instruction yet, go for it
	if (needsExplanation) {
		dispatch({
			show: true,
			type: "ocs_like",
			onAction: doLike,
			onShow: () => {
				OkC.post("/user_guides/OCS_LIKE/understands");
			},
		});
	} else {
		doLike();
	}
}

/**
 * @param  {String} userid - The userid of the user being passed on.
 * @param  {Object} params - Additional params
 * @param  {Bool} params.wasPassed - If, before clicking the link, the user had passed.
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 * @param  {Bool} params.firstMessage - If there was a message.
 * @param  {Bool} params.needsExplanation - Shows a confirmation modal if true.
 */
export function pass(
	userid,
	{
		wasPassed,
		matchPercentage,
		firstMessage,
		needsExplanation,
	} = {},
) {
	if (!userid) { return; }

	// if they already passed this person, do nothing
	if (wasPassed) { return; }

	const handleFailure = () => {
		showError(_str("Unable to pass on them"));
	};

	const doPass = () => {
		OkC.api("/likes/batch", {
			api: 1,
			type: "POST",
			data: {
				source: "PROFILE",
				votes: [
					{
						userid,
						like: false,
						time_from_request: 0,
					},
				],
			},
			success(res) {
				const data = res.results[0];

				if (!data) {
					handleFailure();
					return;
				}

				if (res.likesRemaining === 0) {
					Analytics.logUIEvent(Analytics.eventNames.votedAfterLikesCapLimit, {
						data: {
							"promo id": "alist.likescappaywall",
							"promo design": PROMO_DESIGN.profilephoto,
							"target user id": userid,
							"vote value": "pass",
							source: "profile",
						},
					});
				}

				buttonData[data.userid] = {
					wasLiked: "null",
					wasPassed: "true",
				};

				triggerOkEvent(OKEVENT.Pass, { tuid: data.userid });

				Analytics.logUIEvent(Analytics.eventNames.profileInteraction, {
					type: CUSTOM_EVENT_TYPES.user_content,
					data: getEventData({
						interaction: "pass",
						userid,
						"resulted in mutual like": "no",
						"message flag": firstMessage ? "yes" : "no",
						matchPercentage,
					}),
				});
			},
			error: handleFailure,
		});
	};

	// if they haven't seen the user instruction yet, go for it
	if (needsExplanation) {
		dispatch({
			show: true,
			type: "ocs_pass",
			onAction: doPass,
			onShow: () => {
				OkC.post("/user_guides/OCS_PASS/understands");
			},
		});
	} else {
		doPass();
	}
}

export function decrementNotificationBadge() {
	const $topBadge = $("#navigation .nav-user-image .badge");
	const $menuBadge = $("#navigation .nav-user-menu .badge");

	const notificationNumber = parseInt($topBadge.text(), 10);
	const newNotificationNumber = notificationNumber - 1;

	if (newNotificationNumber === 0) {
		$topBadge.remove();
		$menuBadge.remove();
	} else {
		$topBadge.text(newNotificationNumber);
		$menuBadge.text(newNotificationNumber);
	}
}

/**
 * @param  {String} userid - The userid of the user being unliked.
 * @param  {Object} params - Addl params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 */
export function unlike(userid, { matchPercentage } = {}) {
	if (!userid) { return; }

	OkLike.process({
		target_userid: userid,
		score: 0,
		matchPercentage,
	});
}

/**
 * @param  {String} userid - The userid of the user whose photo is bring reported.
 * @param  {String} photoId - The photoid being reported.
 */
export function reportPhoto(userid, photoId) {
	if (!userid || !photoId) { return; }

	Report.open({
		type: TYPES.PHOTO_ALBUM,
		userid,
		photoId,
	});
}

/**
 * @param  {String} userid   - The userid of the user being hidden.
 * @param  {Object} params - Addl params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 */
export function hide(userid, { matchPercentage } = {}) {
	if (!userid) { return; }

	const hasSeenHideConfirmation = Storage.getItem(HIDE_STORAGE_ITEM);

	const doHide = () => {
		OkC.api(`/profile/${userid}/hide`, {
			api: 1,
			type: "POST",
			success(res) {
				if (!hasSeenHideConfirmation) {
					Storage.setItem(HIDE_STORAGE_ITEM, true);
				}

				triggerOkEvent(OKEVENT.Hide, { tuid: res.userid });

				Analytics.logUIEvent(Analytics.eventNames.profileHide, {
					data: getEventData({
						value: "hid",
						userid,
						matchPercentage,
					}),
				});
			},
			error() {
				showError(_str("Unable to hide them"));
			},
		});
	};

	if (hasSeenHideConfirmation) {
		doHide();
	} else {
		dispatch({
			show: true,
			type: "hide",
			onAction: doHide,
		});
	}
}

export const unmatchSuccess = (
	userid,
	{
		matchPercentage,
		sourceProfile,
	} = {},
) => {
	triggerOkEvent(OKEVENT.Unmatch, { tuid: userid }, { forwardToParent: true });

	Analytics.logUIEvent(Analytics.eventNames.profileBlock, {
		data: getEventData({
			value: "unmatched",
			userid,
			matchPercentage,
		}),
	});

	showError(_str("You've been unmatched."), "success");

	// if you're on their profile, send em to the mailbox
	if (sourceProfile && sourceProfile === userid) {
		goToUrl(`/messages?unmatched_userid=${userid}`);
	}

	// if you're in the mailbox, update that shit
	if (typeof (MessagesPage) !== "undefined" && MessagesPage !== null) {
		MessagesPage.handleAppEvent("userBlockedEvent", { userid });
	}

	// if the global mailbox exists, update that shit too
	if (typeof (MessageDropdown) !== "undefined" && MessageDropdown !== null) {
		MessageDropdown.handleEvent("userBlockedEvent", { userid });
	}
};

/**
 * @param  {String} userid   - The userid of the user being unmatched.
 * @param  {Int} matchPercentage - The match percentage with this user.
 */
export function unmatch(
	userid, {
		matchPercentage,
		sourceProfile,
	} = {},
) {
	if (!userid) { return; }

	const doUnmatch = () => {
		OkC.api(`/profile/${userid}/block`, {
			api: 1,
			type: "POST",
			success(res) {
				unmatchSuccess(res.userid, {
					matchPercentage,
					sourceProfile,
				});
			},
			error() {
				showError(_str("Unable to unmatch with them."));
			},
		});
	};

	dispatch({
		show: true,
		type: "unmatch",
		onAction: doUnmatch,
	});
}


/**
 * @param  {String} userid   - The userid of the user being unhidden.
 * @param  {Object} params - Additional params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 */
export function unhide(userid, { matchPercentage } = {}) {
	if (!userid) { return; }

	OkC.api(`/profile/${userid}/unhide`, {
		api: 1,
		type: "POST",
		success(res) {
			triggerOkEvent(OKEVENT.Unhide, { tuid: res.userid });

			Analytics.logUIEvent(Analytics.eventNames.profileHide, {
				data: getEventData({
					value: "unhid",
					userid,
					matchPercentage,
				}),
			});
		},
		error() {
			showError(_str("Unable to un-hide them"));
		},
	});
}

/**
 * @param  {String} userid   - The userid of the user being favorited.
 * @param  {Object} params - Additional params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 */
export function favorite(userid, { matchPercentage } = {}) {
	if (!userid) { return; }

	OkC.api(`/profile/${userid}/favorite`, {
		api: 1,
		type: "POST",
		success(res) {
			triggerOkEvent(OKEVENT.Favorite, { tuid: res.userid });

			Analytics.logUIEvent(Analytics.eventNames.profileBookmark, {
				data: getEventData({
					value: "bookmarked",
					userid,
					matchPercentage,
				}),
			});
		},
		error() {
			showError(_str("Unable to bookmark them"));
		},
	});
}

/**
 * @param  {String} userid   - The userid of the user being unfavorited.
 * @param  {Object} params - Additional params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 */
export function unfavorite(userid, { matchPercentage } = {}) {
	if (!userid) { return; }

	OkC.api(`/profile/${userid}/unfavorite`, {
		api: 1,
		type: "POST",
		success(res) {
			triggerOkEvent(OKEVENT.Unfavorite, { tuid: res.userid });

			Analytics.logUIEvent(Analytics.eventNames.profileBookmark, {
				data: getEventData({
					value: "unbookmarked",
					userid,
					matchPercentage,
				}),
			});
		},
		error() {
			showError(_str("Unable to un-bookmark them"));
		},
	});
}


/**
 * An intentional visit for invisible browsers
 * @param  {String} userid   - The userid of the user being visited.
 */
export function visit(userid) {
	if (!userid) { return; }

	OkC.api(`/profile/${userid}/visit`, {
		api: 1,
		data: {
			force: true,
		},
		type: "POST",
		success(res) {
			triggerOkEvent(OKEVENT.Visit, { tuid: res.userid });
		},
		error() {
			showError(_str("Unable to reveal your visit to them"));
		},
	});
}

export const blockSuccess = (
	userid,
	{
		skipModal,
		matchPercentage,
		sourceProfile,
	} = {},
) => {
	triggerOkEvent(OKEVENT.Block, { tuid: userid }, { forwardToParent: true });

	Analytics.logUIEvent(Analytics.eventNames.profileBlock, {
		data: getEventData({
			value: "blocked",
			userid,
			matchPercentage,
		}),
	});

	// if you're on their profile, send em to the mailbox
	if (sourceProfile && sourceProfile === userid) {
		goToUrl("/messages");
	}

	// if you're in the mailbox, update that shit
	if (typeof (MessagesPage) !== "undefined" && MessagesPage !== null) {
		MessagesPage.handleAppEvent("userBlockedEvent", { userid });
	}

	// if the global mailbox exists, update that shit too
	if (typeof (MessageDropdown) !== "undefined" && MessageDropdown !== null) {
		MessageDropdown.handleEvent("userBlockedEvent", { userid });
	}

	if (!skipModal) {
		showError(_str("They’ve been blocked."), "success");
	}
};

/**
 * @param {String}  userid    - The userid of the user being blocked.
 * @param  {Object} params - Additional params
 * @param  {Int} params.matchPercentage - The match percentage with this user.
 * @param {Boolean} params.skipModal - Sometimes you just gotta block the person
 *                              without the dobule confirmation. Pass true
 *                              to do that.
 */
export function block(
	userid,
	{
		skipModal,
		matchPercentage,
		sourceProfile,
	} = {},
) {
	if (!userid) { return; }

	const doBlock = () => {
		OkC.api(`/profile/${userid}/block`, {
			api: 1,
			type: "POST",
			success(res) {
				blockSuccess(res.userid, {
					skipModal,
					matchPercentage,
					sourceProfile,
				});
			},
			error() {
				if (!skipModal) {
					showError(_str("Unable to block them"));
				}
			},
		});
	};

	if (!skipModal) {
		dispatch({
			show: true,
			type: "block",
			onAction: doBlock,
		});
	} else {
		doBlock();
	}
}

/**
 * @param  {String} userid - The userid of the user being unblocked.
 */
export function unblock(userid) {
	if (!userid) { return; }

	OkC.api(`/profile/${userid}/unblock`, {
		api: 1,
		type: "POST",
		success(res) {
			triggerOkEvent(OKEVENT.Unblock, { tuid: res.userid });
			triggerOkEvent(OKEVENT.Unhide, { tuid: res.userid });
			showError(_str("They’ve been unblocked."), "success");

			Analytics.logUIEvent(Analytics.eventNames.profileBlock, {
				data: getEventData({
					value: "unblocked",
					userid,
				}),
			});
		},
		error() {
			showError(_str("Unable to unblock them"));
		},
	});
}

/**
 * @param  {string} username - The username of the non-logged in user in the couple.
 */
export function link(username) {
	if (!username) { return; }

	OkC.api("/profile/me/link", {
		type: "POST",
		api: 1,
		data: {
			username,
		},
		success(res) {
			if (res.result === "success") {
				triggerOkEvent(OKEVENT.Link, { tuid: res.userid });
			} else {
				showError(_str("Unable to link them"));
			}
		},
	});
}

export function linkReject(userid, requestTimestamp) {
	if (!userid || !requestTimestamp) { return; }

	OkC.post("/profile/me/link/reject", {
		api: 1,
		data: {
			userid,
			requestTimestamp,
		},
		success(res) {
			if (res.result === "success") {
				triggerOkEvent(OKEVENT.LinkReject, { tuid: res.userid });
			} else {
				showError(_str("Unable to reject the link"));
			}
		},
	});
}

export function unlink() {
	OkC.delete("/profile/me/link", {
		api: 1,
		success(res) {
			if (res.result === "success") {
				triggerOkEvent(OKEVENT.Unlink, { tuid: res.userid });
			} else {
				showError(_str("Unable to unlink them"));
			}
		},
	});
}

const ProfileActions = {
	block,
	favorite,
	hide,
	pass,
	like,
	link,
	linkReject,
	unfavorite,
	unblock,
	unhide,
	unmatch,
	unlike,
	unlink,
	visit,
	decrementNotificationBadge,

	helpers: {
		getEventData,
	},
};

// Attach it to the window instead of exporting it because it has to go
// in the common bundle.
window.ProfileActions = ProfileActions;

// Browser stuff down here.
if (typeof window !== "undefined") {
	const ProfileActionsDOM = {
		init() {
			this._mountConfirmationModal();
			this._bindQuickActions();
		},

		_mountConfirmationModal() {
			const mountNode = document.createElement("div");
			mountNode.id = "confirmationModal";
			document.body.appendChild(mountNode);
			ReactDOM.render(<ConfirmationModal />, mountNode);
		},

		_bindQuickActions() {
			const attr = "[data-profile-action]";

			$("body").delegate(attr, "click", (e) => {
				e.preventDefault();

				if (e.currentTarget.classList.contains("disabled") ||
					document.body.classList.contains("no-actions")) return;

				const el = e.currentTarget;
				const action = el.getAttribute("data-profile-action");
				const userid = el.getAttribute("data-tuid");
				const confirmed = el.getAttribute("data-confirmed");
				let wasLiked = el.getAttribute("data-was-liked");
				let wasPassed = el.getAttribute("data-was-passed");

				if (buttonData[userid]) {
					wasLiked = buttonData[userid].wasLiked;
					wasPassed = buttonData[userid].wasPassed;
				}

				let matchPercentage = el.getAttribute("data-match-percentage");

				if (matchPercentage) {
					matchPercentage = parseInt(matchPercentage, 10);
				}

				if (ProfileActions[action] && userid) {
					ProfileActions[action](userid, {
						matchPercentage,
						wasLiked: wasLiked === "true",
						wasPassed: wasPassed === "true",
						confirmed: confirmed === "true",
					});
				}
			});
		},
	};

	OnDOMReady(ProfileActionsDOM.init.bind(ProfileActionsDOM));
}



// WEBPACK FOOTER //
// ./src/shared/util/ProfileActions.js